import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  StyleSheet, ScrollView, View, Platform, RefreshControl, Keyboard,
} from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import { isPromptNoindex } from '../../../../shared/public/prompt-indexing.cjs';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import ToolForm from '@/components/Tools/ToolForm';
import ToolExamplesPanel from '@/components/Tools/ToolExamplesPanel';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import PromptRecipeBlock from '@/components/Prompts/PromptRecipeBlock';
import PromptExampleMedia from '@/components/Prompts/PromptExampleMedia';
import PromptGenerateButton from '@/components/Prompts/PromptGenerateButton';
import PromptRelatedStrip from '@/components/Prompts/PromptRelatedStrip';
import CatalogDetailPageSkeleton from '@/components/Catalog/CatalogDetailPageSkeleton';
import PromptRelatedTools from '@/components/Prompts/PromptRelatedTools';
import PromptFavoriteHeart from '@/components/Prompts/PromptFavoriteHeart';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import Badge from '@/components/Typography/Badge';
import { getPaletteForCategorySlug } from '@/components/Tools/toolDetailHelpers';
import { usePromptStore, useToastStore, useModalStore, useModelStore } from '@/store';
import { promptsApi } from '@/lib/api';
import { promptApi as promptApiSsr } from '@/lib/promptApi';
import useRequireAuth from '@/hooks/useRequireAuth';
import { isPlusRequiredApiError, openModelAccessPaywall } from '@/constants/utils/trial';

const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';
const VIEWED_PROMPT_SLUGS = new Set();
const PROMPT_VIDEO_WIDTH = 1536;
const PROMPT_VIDEO_HEIGHT = 1024;

function guessVideoMimeType(url = '') {
  const ext = String(url).split('?')[0].split('.').pop()?.toLowerCase();
  if (ext === 'webm') return 'video/webm';
  if (ext === 'mov') return 'video/quicktime';
  return 'video/mp4';
}

const _staticPromptCache = {};
export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered.
  // Returning [] keeps `expo export` fast and avoids hammering the API at
  // build time. SEO meta is injected at request time; see backend appRoutes.
  return [];
}

const createStyles = (colors, isXS, insetBottom = 0) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.solid_color_primary },
  scroll: { flex: 1, width: '100%' },
  scrollContent: { width: '100%', paddingBottom: insetBottom + 80 },
  inner: {
    padding: 15,
    paddingTop: 5,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
    gap: 18,
  },
  section: { gap: 12 },
  sectionTitle: { marginBottom: 4 },
  // Matches the cover image + hero radius (14) for a cohesive card stack.
  formCard: {
    padding: isXS ? 16 : 20,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    gap: 14,
  },
  footerWrap: { paddingHorizontal: isXS ? 16 : 24, paddingTop: 24 },
});

function PromptDetailPage({ slug }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { addToast } = useToastStore(s => s.actions);
  const requireAuth = useRequireAuth();

  const livePrompt = usePromptStore(s => s.promptsBySlug[slug]);
  const staticPrompt = slug ? _staticPromptCache[slug] : null;
  const prompt = livePrompt || staticPrompt || null;
  const detailError = usePromptStore(s => s.detailErrorBySlug[slug]);
  const fetchPromptBySlug = usePromptStore(s => s.fetchPromptBySlug);
  const isRunning = usePromptStore(s => s.isRunningBySlug[slug]);
  const runPrompt = usePromptStore(s => s.runPrompt);

  const formRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);

  const styles = useMemo(() => createStyles(colors, isXS, insets.bottom), [colors, isXS, insets.bottom]);

  const palette = useMemo(
    () => getPaletteForCategorySlug(prompt?.category?.slug || ''),
    [prompt?.category?.slug],
  );

  const visibleFields = useMemo(
    () => (Array.isArray(prompt?.fields) ? prompt.fields : []).filter(f => f?.flags?.isVisibleToUsers !== false),
    [prompt?.fields],
  );

  const examples = useMemo(() => {
    const list = Array.isArray(prompt?.examples) ? prompt.examples : [];
    const fieldNames = new Set(visibleFields.map(f => f?.name).filter(Boolean));
    return list
      .filter(ex => ex && typeof ex === 'object')
      .map(ex => Object.fromEntries(
        Object.entries(ex).filter(([key, value]) => (
          fieldNames.has(key) && value !== undefined && value !== null && value !== ''
        )),
      ))
      .filter(ex => Object.keys(ex).length > 0);
  }, [prompt, visibleFields]);

  const pageCopy = useMemo(
    () => (Array.isArray(prompt?.meta?.pageCopy) ? prompt.meta.pageCopy : []),
    [prompt],
  );

  const buildFaqGraphNode = useCallback((blocks) => {
    const mainEntity = (blocks || [])
      .filter(block => block?.type === 'faq' && Array.isArray(block.items))
      .flatMap(block => block.items)
      .map(item => ({
        '@type': 'Question',
        name: item?.question || item?.title || item?.q || '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: item?.answer || item?.description || item?.copy || item?.a || '',
        },
      }))
      .filter(q => q.name && q.acceptedAnswer.text);
    if (!mainEntity.length) return null;
    return { '@type': 'FAQPage', mainEntity };
  }, []);

  const seoStructuredData = useMemo(() => {
    if (!prompt?.slug) return null;
    const metaDescription = prompt.meta?.description || prompt.description || '';
    const seoCanonical = `/prompts/${prompt.slug}`;
    const pageUrl = `https://gab.ai${seoCanonical}`;
    const isVideoPrompt = prompt.outputType === 'video' && !!prompt.exampleVideoUrl;
    const shareImage = prompt.exampleThumbnailUrl || prompt.meta?.ogImage || prompt.exampleMediaUrl || '';
    const graph = [];

    if (isVideoPrompt) {
      graph.push({
        '@type': 'VideoObject',
        name: prompt.meta?.title || prompt.name,
        description: metaDescription,
        url: pageUrl,
        contentUrl: prompt.exampleVideoUrl,
        embedUrl: pageUrl,
        thumbnailUrl: shareImage || undefined,
        uploadDate: prompt.updatedAt || prompt.createdAt || undefined,
        width: PROMPT_VIDEO_WIDTH,
        height: PROMPT_VIDEO_HEIGHT,
        encodingFormat: guessVideoMimeType(prompt.exampleVideoUrl),
        creator: { '@type': 'Organization', name: 'Gab AI', url: 'https://gab.ai' },
      });
    }

    graph.push(
      {
        '@type': 'SoftwareApplication',
        name: prompt.name,
        description: metaDescription,
        url: pageUrl,
        applicationCategory: prompt.category?.name || 'MultimediaApplication',
        applicationSubCategory: prompt.outputType || undefined,
        operatingSystem: 'Web, iOS, Android',
        ...(shareImage ? { image: shareImage } : {}),
        provider: {
          '@type': 'Organization',
          name: 'Gab AI Inc.',
          url: 'https://gab.ai',
        },
        ...(prompt.stats?.runCount > 0 ? {
          interactionStatistic: {
            '@type': 'InteractionCounter',
            interactionType: { '@type': 'UseAction' },
            userInteractionCount: prompt.stats.runCount,
          },
        } : {}),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
          { '@type': 'ListItem', position: 2, name: 'Prompt Library', item: 'https://gab.ai/prompts' },
          {
            '@type': 'ListItem',
            position: 3,
            name: prompt.name,
            item: pageUrl,
          },
        ],
      },
    );
    const faqNode = buildFaqGraphNode(pageCopy);
    if (faqNode) graph.push(faqNode);
    return { '@context': 'https://schema.org', '@graph': graph };
  }, [prompt, pageCopy, buildFaqGraphNode]);

  useEffect(() => {
    if (!slug) return;
    fetchPromptBySlug(slug).catch(() => {});
    if (!VIEWED_PROMPT_SLUGS.has(slug)) {
      VIEWED_PROMPT_SLUGS.add(slug);
      promptsApi.trackView(slug).catch(() => {});
    }
  }, [slug, fetchPromptBySlug]);

  const onRefresh = useCallback(async () => {
    if (!slug) return;
    setRefreshing(true);
    try {
      await fetchPromptBySlug(slug, { force: true });
    } finally {
      setRefreshing(false);
    }
  }, [slug, fetchPromptBySlug]);

  const { openModal } = useModalStore(s => s.actions);
  const getModelById = useModelStore(s => s.getModelById);

  const handleSubmit = useCallback(async (inputs, opts = {}) => {
    if (!slug) return;
    const ran = requireAuth(() => true, {
      source: 'prompt_run',
      title: `Sign in to run ${prompt?.name || 'this recipe'}`,
      subtitle: 'Runs create a chat thread tied to your account so you can pick up where you left off.',
      icon: 'play',
    });
    if (ran !== true) return;
    if (IS_TOUCH_NATIVE) Keyboard.dismiss();
    try {
      const conversationId = await runPrompt(slug, {
        inputs,
        modelId: opts.modelId || null,
        fileIds: opts.fileIds || [],
      });
      if (conversationId) {
        router.push(`/c/${conversationId}`);
      } else {
        addToast(t('pages.prompts.runStarted') || 'Generation started — open your chats to view progress.', 'success');
      }
    } catch (e) {
      if (isPlusRequiredApiError(e)) {
        const model = opts.modelId ? getModelById?.(opts.modelId) : null;
        openModelAccessPaywall(openModal, { model });
        return;
      }
      addToast(e?.message || t('pages.prompts.runFailed'), 'error');
    }
  }, [slug, runPrompt, requireAuth, prompt?.name, router, addToast, t, openModal, getModelById]);

  const runLabel = prompt?.runButtonLabel?.trim() || (t('pages.prompts.generate') || 'Generate');
  const runIcon = prompt?.runButtonIcon?.trim() || 'wand-magic-sparkles';

  // Don't guess a model from the global catalog until GET /json/prompts/:slug
  // returns capability-enriched picker data (avoids flashing bg removers, etc.).
  const deferPromptModelFallback = useMemo(() => {
    if (!prompt || (prompt.outputType !== 'image' && prompt.outputType !== 'video')) return false;
    if (livePrompt?.defaultModel?.id || (livePrompt?.suggestedModels?.length > 0)) return false;
    if (detailError) return false;
    return true;
  }, [prompt, livePrompt, detailError]);

  // Match tool detail hero pills — default Badge uses solid_color_toast (#232425)
  // which reads as solid black on the light hero card.
  const heroBadgePrefix = useMemo(() => {
    const hasOutput = !!prompt?.outputType;
    const hasUpload = prompt?.inputType === 'image';
    const hasNew = !!prompt?.flags?.isNew;
    if (!hasOutput && !hasUpload && !hasNew) return null;

    const bg = colors.solid_color_primary;
    const pillStyle = {
      borderWidth: StyleSheet.hairlineWidth,
      borderRadius: 999,
      minHeight: 22,
      height: undefined,
      paddingVertical: 3,
      paddingHorizontal: 10,
      backgroundColor: bg,
      borderColor: colors.border_color_secondary,
    };

    return (
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
        {hasOutput ? (
          <Badge
            text={prompt.outputType}
            isSmall
            textColor="secondary"
            backgroundColor={bg}
            style={pillStyle}
          />
        ) : null}
        {hasUpload ? (
          <Badge
            text={t('pages.prompts.uploadRequired') || 'Upload'}
            isSmall
            textColor="secondary"
            backgroundColor={bg}
            style={pillStyle}
          />
        ) : null}
        {hasNew ? (
          <Badge
            text={t('pages.tools.badgeNew') || 'New'}
            isSmall
            textColor="secondary"
            backgroundColor={bg}
            style={pillStyle}
          />
        ) : null}
      </View>
    );
  }, [prompt?.outputType, prompt?.inputType, prompt?.flags?.isNew, colors, t]);

  if (!slug) return <Redirect href="/prompts" />;

  const noindex = !prompt || isPromptNoindex(prompt);
  // The first client render precedes the store's fetch effect. Keep the real
  // server head and readable content until the record arrives; a loading head
  // would replace an eligible title/robots and retire the server body early.
  if (!prompt && !detailError) {
    return <CatalogDetailPageSkeleton variant="prompt" />;
  }

  if (!prompt && detailError) {
    return (
      <View style={styles.container}>
        {noindex && <MetaHead noindex canonical={`/prompts/${slug}`} />}
        <StatusContainer type="error" message={detailError} />
      </View>
    );
  }

  if (!prompt) return <Redirect href="/prompts" />;

  const metaTitle = prompt.meta?.title || `${prompt.name} — Prompt Library`;
  const metaDescription = prompt.meta?.description || prompt.description || '';
  const seoKeywords = prompt.meta?.keywords
    || [prompt.name, prompt.category?.name, prompt.outputType, 'AI prompt', 'Gab AI'].filter(Boolean).join(', ');
  const seoCanonical = `/prompts/${slug}`;
  const isVideoPrompt = prompt.outputType === 'video' && !!prompt.exampleVideoUrl;
  const shareImage = prompt.exampleThumbnailUrl || prompt.meta?.ogImage || prompt.exampleMediaUrl || undefined;

  return (
    <View style={styles.container}>
      <MetaHead
        noindex={noindex}
        title={metaTitle}
        description={metaDescription}
        canonical={seoCanonical}
        keywords={seoKeywords}
        image={shareImage}
        imageWidth={isVideoPrompt ? PROMPT_VIDEO_WIDTH : undefined}
        imageHeight={isVideoPrompt ? PROMPT_VIDEO_HEIGHT : undefined}
        type={isVideoPrompt ? 'video.other' : 'website'}
        video={isVideoPrompt ? {
          url: prompt.exampleVideoUrl,
          type: guessVideoMimeType(prompt.exampleVideoUrl),
          width: PROMPT_VIDEO_WIDTH,
          height: PROMPT_VIDEO_HEIGHT,
        } : undefined}
        structuredData={seoStructuredData}
        lastModified={prompt.updatedAt}
      />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        refreshControl={(
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.text_color_secondary} />
        )}
      >
        <View style={styles.inner}>
          <CatalogDetailHero
            borderRadius={14}
            backLink={!IS_TOUCH_NATIVE ? {
              label: t('pages.prompts.allPrompts') || 'All recipes',
              onPress: () => router.push('/prompts'),
            } : null}
            categorySlug={prompt.category?.slug}
            gradientSlug={prompt.slug}
            palette={palette}
            headline={prompt.name}
            subtitle={prompt.description}
            catalogCategory={prompt.category?.slug ? {
              label: prompt.category.name,
              icon: 'folder',
              href: `/prompts/category/${encodeURIComponent(prompt.category.slug)}`,
            } : null}
            favoriteAccessory={<PromptFavoriteHeart prompt={prompt} />}
            badgeRowPrefix={heroBadgePrefix}
          />

          <PromptExampleMedia prompt={prompt} />

          <PromptRecipeBlock
            systemPrompt={prompt.systemPrompt}
            promptTemplate={prompt.promptTemplate}
          />

          {examples.length > 0 ? (
            <View style={styles.section}>
              <Text weight="bold" size="m" style={styles.sectionTitle}>
                {t('pages.prompts.examplesTitle')}
              </Text>
              <ToolExamplesPanel
                examples={examples}
                visibleFields={visibleFields}
                palette={palette}
                isRunning={isRunning}
                onApply={(ex, index) => formRef.current?.applyExample?.(ex, index)}
              />
            </View>
          ) : null}

          <View style={styles.section}>
            <Text weight="bold" size="m" style={styles.sectionTitle}>
              {t('pages.prompts.configureTitle') || 'Your inputs'}
            </Text>
            <View style={styles.formCard}>
              <ToolForm
                ref={formRef}
                tool={prompt}
                catalogKind="prompt"
                isRunning={isRunning}
                onSubmit={handleSubmit}
                hideSubmitButton
                showCostFooter
                hideExamples
                deferModelFallback={deferPromptModelFallback}
              />
              <PromptGenerateButton
                label={runLabel}
                loadingLabel={t('pages.prompts.generating') || 'Starting…'}
                icon={runIcon}
                isLoading={isRunning}
                onPress={() => formRef.current?.submit?.()}
              />
            </View>
          </View>

          {pageCopy.length > 0 ? (
            <ToolPageCopy blocks={pageCopy} palette={palette} />
          ) : null}

          <PromptRelatedTools slug={slug} />

          <PromptRelatedStrip slug={slug} />
        </View>
        <View style={styles.footerWrap}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
}

export default function PromptDetailRoute() {
  const params = useLocalSearchParams();
  const slug = typeof params?.slug === 'string'
    ? params.slug
    : Array.isArray(params?.slug) ? params.slug[0] : null;
  return <PromptDetailPage slug={slug} />;
}
